import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, DialogActions, Typography } from "@mui/material";
import moment from "moment";
import { ActionTypes } from "../../../../OrderFlow/OrderFlowProvider/OrderFlowActionTypes";
import { ModuleNames, } from "../../../../OrderFlow/OrderFlowProvider/OrderFlowTypes";
import { LogLevel } from "../../../hooks/useLogging/Logging-types";
import { ModalContent } from "../../Modal";
import { clone } from "../../../utils/clone";
/**
 * If it is a subscription, the template and meals are decremented. If it is not a subscription, only the meals are decremented.
 *
 * @param cartMeals
 * @param cartTemplate
 * @param dispatch
 * @returns
 */
export const handleEditMinusClick = (cartMeals, cartTemplate, dispatch) => (canAddRemoveMeals, canAddRemoveRecipes, meal, assignment, date) => {
    //Template only exists for subscription orders so we need to check if there is one before editing it
    if (cartTemplate.length > 0) {
        const { updatedTemplate, updatedMeals } = getUpdatedTemplateAndMeals(cartTemplate, cartMeals, meal, date, assignment, canAddRemoveMeals, canAddRemoveRecipes, false);
        dispatch({ type: ActionTypes.SET_TEMPLATE, payload: updatedTemplate });
        dispatch({ type: ActionTypes.SET_MEALS, payload: updatedMeals });
    }
    else {
        const updatedMeals = getUpdatedMeals(cartMeals, meal, date, assignment, canAddRemoveMeals, canAddRemoveRecipes, false);
        dispatch({ type: ActionTypes.SET_MEALS, payload: updatedMeals });
    }
};
/**
 * If it is a subscription, the template and meals are incremented. If it is not a subscription, only the meals are incremented.
 *
 * @param cartMeals
 * @param cartTemplate
 * @param dispatch
 * @returns
 */
export const handleEditAddClick = (cartMeals, cartTemplate, dispatch) => (canAddRemoveMeals, canAddRemoveRecipes, meal, assignment, date) => {
    //Template only exists for subscription orders so we need to check if there is one before editing it
    if (cartTemplate.length > 0) {
        const { updatedTemplate, updatedMeals } = getUpdatedTemplateAndMeals(cartTemplate, cartMeals, meal, date, assignment, canAddRemoveMeals, canAddRemoveRecipes, true);
        dispatch({ type: ActionTypes.SET_TEMPLATE, payload: updatedTemplate });
        dispatch({ type: ActionTypes.SET_MEALS, payload: updatedMeals });
    }
    else {
        const updatedMeals = getUpdatedMeals(cartMeals, meal, date, assignment, canAddRemoveMeals, canAddRemoveRecipes, true);
        dispatch({ type: ActionTypes.SET_MEALS, payload: updatedMeals });
    }
};
/**
 * Updates the template quantity for meals without recipe assignments
 *
 * @param cartTemplate
 * @param dispatch
 * @returns
 */
export const handleUnselectedMealClick = (cartMeals, cartTemplate, dispatch) => (canAddRemoveMeals, meal, date, increment) => {
    const updatedTemplate = cartTemplate.map((templateMeal) => {
        if (templateMeal.DayOfWeek === moment(date).day() && templateMeal.ProgramMealId === meal.programMealId) {
            if (canAddRemoveMeals) {
                increment ? (templateMeal.Quantity += 1) : (templateMeal.Quantity -= 1);
            }
        }
        return templateMeal;
    });
    const updatedMeals = cartMeals
        .map((cartMeal) => {
        if (moment(cartMeal.MealDate).isSame(moment(date), "day") && cartMeal.ProgramMealId === meal.programMealId) {
            if (canAddRemoveMeals) {
                increment ? (cartMeal.Quantity += 1) : (cartMeal.Quantity -= 1);
            }
        }
        return cartMeal;
    })
        .filter((c) => c.Quantity > 0);
    dispatch({ type: ActionTypes.SET_TEMPLATE, payload: updatedTemplate });
    dispatch({ type: ActionTypes.SET_MEALS, payload: updatedMeals });
};
/**
 * Check requirements. Save edits to order flow session. Set order info to refresh pricing
 *
 * @param checkRequirements
 * @param showModal
 * @param closeModal
 * @param navigate
 * @param location
 * @param store
 * @param dispatch
 * @param mealsBeforeEditing
 * @param setIsEditMode
 * @param setMealsBeforeEditing
 * @param setOrderInfo
 * @param datadogLog
 * @param continueToCheckout
 * @returns
 */
export const saveEdits = (checkRequirements, showModal, closeModal, navigate, location, store, dispatch, mealsBeforeEditing, setIsEditMode, setMealsBeforeEditing, setOrderInfo, 
// TODO REPLACE AND REMOVE ALL INSTANCES OF AND REFERENCES TO pricingBreakdown WITH EQUIVALENT FROM BALANCE OBJECT
setPricingBreakdown, setBalance, datadogLog, continueToCheckout, setLoading, updatePayInitiation, doesPaymentIntentExist) => () => {
    setLoading(true);
    const requirementsResponse = checkRequirements(store.model.requirements, store.model.SkipPayment, store.cart.meals, store.cart.products, store.model.PostalCode, store.cart.currentPage, store.cart.daysOfWeek, store.model, store.cart);
    if (!(requirementsResponse === null || requirementsResponse === void 0 ? void 0 : requirementsResponse.meetsRequirements)) {
        setLoading(false);
        showModal({
            title: "Requirement",
            content: (_jsx(ModalContent, { children: _jsx(Typography, { children: `${requirementsResponse === null || requirementsResponse === void 0 ? void 0 : requirementsResponse.errorMessage}. Would you like to go back or cancel your edits?` }) })),
            actions: (_jsxs(DialogActions, { children: [_jsx(Button, Object.assign({ variant: "outlined", color: "primary", onClick: () => {
                            var _a, _b;
                            //When the edits do not meet requirements, we send the customer back to the page of Order Flow that contains the requirements module
                            const pageWithRequirementsModule = (_b = (_a = store.model.Flow.FlowModules.find((fm) => fm.ModuleName === ModuleNames.REQUIREMENTS)) === null || _a === void 0 ? void 0 : _a.Page) !== null && _b !== void 0 ? _b : 0;
                            navigate(`/${store.model.Sku}/OrderFlow`, {
                                state: Object.assign(Object.assign({}, location.state), { page: pageWithRequirementsModule }),
                            });
                            setIsEditMode(false);
                            closeModal();
                        } }, { children: "Go back" })), _jsx(Button, Object.assign({ onClick: () => {
                            setIsEditMode(false);
                            if (mealsBeforeEditing) {
                                dispatch({ type: ActionTypes.SET_MEALS, payload: mealsBeforeEditing });
                            }
                            setMealsBeforeEditing(undefined);
                            closeModal();
                        } }, { children: "Cancel edits" }))] })),
            closeModalFunc: () => {
                setLoading(false);
                closeModal();
            },
        });
    }
    else {
        const editedMealsToSave = getMealsWithQuantity(store.cart.meals);
        dispatch({ type: ActionTypes.SET_MEALS, payload: editedMealsToSave });
        continueToCheckout(store.cart, store.model)
            .then((res) => {
            if (res && res.success && res.continueToCheckout) {
                const orderFlowState = {
                    // TODO REPLACE AND REMOVE ALL INSTANCES OF AND REFERENCES TO pricingBreakdown WITH EQUIVALENT FROM BALANCE OBJECT
                    pricingBreakdown: res.pricingBreakdown,
                    balance: res.balance,
                    orderInfo: res.orderInfo,
                    sku: store.model.Sku,
                    location: store.model.Location,
                };
                location.state.orderFlowState = orderFlowState;
                setOrderInfo(res.orderInfo);
                // TODO REPLACE AND REMOVE ALL INSTANCES OF AND REFERENCES TO pricingBreakdown WITH EQUIVALENT FROM BALANCE OBJECT
                setPricingBreakdown(res.pricingBreakdown);
                setBalance(res.balance);
                if (doesPaymentIntentExist)
                    updatePayInitiation(res.balance.remainingBalance);
            }
            else {
                datadogLog("Failed to navigate to checkout", LogLevel.ERROR);
            }
        })
            .catch((er) => {
            datadogLog("Failed to navigate to checkout", LogLevel.ERROR, undefined, er);
        })
            .finally(() => {
            setIsEditMode(false);
            setLoading(false);
        });
    }
};
/**
 * While editing we show the customer meals and recipe assignments with 0 quantity so they have the option to add them back.
 * Once the edits are saved we have to remove the meals and recipe assignments with 0 quantity.
 * @param cartMeals
 */
const getMealsWithQuantity = (cartMeals) => {
    //Get meals with quantity > 0
    const mealsWithQuantity = cartMeals.filter((cartMeal) => cartMeal.Quantity > 0);
    //Get meals that have recipe assignments with quantity > 0
    const recipeAssignmentsWithQuantity = mealsWithQuantity.map((cartMeal) => {
        var _a;
        return Object.assign(Object.assign({}, cartMeal), { RecipeAssignments: (_a = cartMeal.RecipeAssignments) === null || _a === void 0 ? void 0 : _a.filter((ra) => ra.Quantity > 0) });
    });
    return recipeAssignmentsWithQuantity;
};
/**
 * Updates the template, meals and recipe assignments. Returns the updated template and meals to be dispatched
 *
 * @param cartTemplate
 * @param cartMeals
 * @param meal
 * @param date
 * @param assignment
 * @param canAddRemoveMeals
 * @param canAddRemoveRecipes
 * @param increment
 * @returns
 */
const getUpdatedTemplateAndMeals = (cartTemplate, cartMeals, meal, date, assignment, canAddRemoveMeals, canAddRemoveRecipes, increment) => {
    const updatedTemplate = cartTemplate.map((templateMeal) => {
        if (templateMeal.DayOfWeek === moment(date).day() && templateMeal.ProgramMealId === meal.programMealId) {
            if (canAddRemoveMeals) {
                increment ? (templateMeal.Quantity += 1) : (templateMeal.Quantity -= 1);
            }
        }
        return templateMeal;
    });
    const updatedMeals = getUpdatedMeals(cartMeals, meal, date, assignment, canAddRemoveMeals, canAddRemoveRecipes, increment);
    return { updatedTemplate, updatedMeals };
};
/**
 * Updates a meal and its recipe assignments. Returns the updated meals to be dispatched
 *
 * @param cartMeals
 * @param meal
 * @param date
 * @param assignment
 * @param canAddRemoveMeals
 * @param canAddRemoveRecipes
 * @param increment
 * @returns
 */
const getUpdatedMeals = (cartMeals, meal, date, assignment, canAddRemoveMeals, canAddRemoveRecipes, increment) => {
    const updatedMeals = cartMeals.map((cartMeal) => {
        var _a, _b, _c;
        if (moment(cartMeal.MealDate).isSame(moment(date), "day") && cartMeal.ProgramMealId === meal.programMealId) {
            const recipeAssignment = (_a = cartMeal === null || cartMeal === void 0 ? void 0 : cartMeal.RecipeAssignments) === null || _a === void 0 ? void 0 : _a.find((recipe) => recipe.RecipeId === assignment.recipeId && recipe.AssignmentId === assignment.assignmentId);
            if (!recipeAssignment)
                return cartMeal;
            const totalAssignmentQuantity = (_c = (_b = cartMeal.RecipeAssignments) === null || _b === void 0 ? void 0 : _b.reduce((total, assignment) => total + assignment.Quantity, 0)) !== null && _c !== void 0 ? _c : 0;
            //When a meal is added or removed we need to do the same with the recipe assignment
            if (canAddRemoveMeals) {
                increment ? (cartMeal.Quantity += 1) : (cartMeal.Quantity -= 1);
                increment ? (recipeAssignment.Quantity += 1) : (recipeAssignment.Quantity -= 1);
            }
            if (!increment && canAddRemoveRecipes) {
                recipeAssignment.Quantity -= 1;
            }
            //Only add another recipe if we haven't completed assignment
            if (increment && canAddRemoveRecipes && totalAssignmentQuantity < cartMeal.Quantity) {
                recipeAssignment.Quantity += 1;
            }
        }
        return cartMeal;
    });
    return updatedMeals;
};
/**
 * Set the current recipe assignment Id and date to be customized and open the customize modal
 *
 * @param cartMeals
 * @param setMeals
 * @param setAssignmentId
 * @param setIsCustomizeModalOpen
 * @returns
 */
export const onRecipeClick = (cartMeals, setMeals, setAssignmentId, setIsCustomizeModalOpen, setRecipeId, setDate) => (assignmentId, recipeId, date) => {
    setRecipeId(recipeId);
    setMeals(clone(cartMeals));
    setAssignmentId(assignmentId);
    setIsCustomizeModalOpen(true);
    setDate(moment(date));
};
